import { LoadableResponse, Restaurant } from "api-types";
import { StyledButton } from "igfd-ui";

import React, { useRef } from "react";

import {
  Box,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material/";
import AppBar from "@mui/material/AppBar";
import Container from "@mui/material/Container";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import { styled } from "@mui/material/styles";

import {
  MenuOpenRounded,
  MenuRounded,
  OpenInNewOutlined,
} from "@mui/icons-material";

import IgfdBlackLogo from "@dashboard/assets/images/logo/logo-name-black.png";
import { SelectedRestaurant } from "@dashboard/components/header/selectedRestaurant/SelectedRestaurant";
import { config } from "@dashboard/environment/config";
import { useDisclosure } from "@dashboard/hooks/useDisclosure";

import { ManagerAvatar } from "../manager-avatar/ManagerAvatar";

interface HeaderComponentProps {
  isLargeScreen: boolean;
  isLeftDrawerOpen: boolean;
  toggleLeftDrawer: () => void;

  /**
   * Display name of the manager.
   */
  managerDisplayName?: string;

  /**
   * Photo URL of the manager.
   */
  managerPhotoUrl?: string;

  menuUrl: LoadableResponse<string>;

  // Null when no restaurants exists.
  selectedRestaurant: Restaurant | null;

  /**
   * Navigate callback to react-router-dom's history.push
   */
  navigate: (to: string) => void;
  logout: () => void;
}

/**
 * Presentation component for showing the header of the dashboard.
 */
export const HeaderComponent = ({
  isLargeScreen,
  isLeftDrawerOpen,
  toggleLeftDrawer,
  managerDisplayName,
  managerPhotoUrl,
  menuUrl,
  selectedRestaurant,
  navigate,
  logout,
}: HeaderComponentProps) => {
  const managerInitials = (managerDisplayName || "G").charAt(0).toUpperCase(); // G stands for Guest
  return (
    <AppBar
      sx={({ palette }) => ({
        backgroundColor: palette.grey[50],
        color: palette.grey[900],
        boxShadow: "none",
        borderBottom: `1px solid ${palette.grey.A200}`,
      })}
    >
      <Container maxWidth={false}>
        <Toolbar disableGutters>
          {isLargeScreen && <LogoComponent />}
          {selectedRestaurant && !isLargeScreen && (
            // a restaurant should exist for this to be shown
            // this controls restaurant navigation since the side bar isn't visible in mobile.
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={toggleLeftDrawer}
              sx={{
                mr: 1,
              }}
            >
              {(isLeftDrawerOpen && <MenuOpenRounded />) || <MenuRounded />}
            </IconButton>
          )}
          <SelectableRestaurantComponent
            isLargeScreen={isLargeScreen}
            selectedRestaurant={selectedRestaurant}
            menuUrl={menuUrl}
            navigate={navigate}
          />
          {isLargeScreen && (
            <AccountNavComponent
              managerInitials={managerInitials}
              managerPhotoUrl={managerPhotoUrl}
              logout={logout}
              navigate={navigate}
            />
          )}
          {!isLargeScreen && (
            <MobileAccountNavComponent
              navigate={navigate}
              logout={logout}
              managerInitials={managerInitials}
              managerPhotoUrl={managerPhotoUrl}
            />
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

const LogoComponent = ({}: {}) => {
  return (
    <Box
      sx={{
        mr: 4,
        mb: 2,
        display: "flex",
        "&:hover": { cursor: "pointer" },
      }}
      onClick={() => (window.location.href = "https://ingredifind.com/")}
    >
      <img
        src={IgfdBlackLogo}
        style={{
          width: "107px",
          objectFit: "cover",
        }}
      />
    </Box>
  );
};

const SelectableRestaurantComponent = ({
  isLargeScreen,
  selectedRestaurant,
  menuUrl,
  navigate,
}: {
  isLargeScreen: boolean;
  selectedRestaurant: Restaurant | null;
  menuUrl: LoadableResponse<string>;
  navigate: (to: string) => void;
}) => {
  return (
    <Stack direction={"row"} alignItems={"center"} gap={2} sx={{ flexGrow: 1 }}>
      {!selectedRestaurant && (
        <StyledButton
          label={"Create a restaurant"}
          size={"small"}
          onClick={() => navigate("/restaurants/create")}
          sx={({ palette }) => ({
            color: palette.grey[900],
            my: 2,
            display: "flex",
          })}
        />
      )}
      {selectedRestaurant && (
        <SelectedRestaurant
          isLargeScreen={isLargeScreen}
          menuUrl={menuUrl}
          selectedRestaurant={selectedRestaurant}
        />
      )}
    </Stack>
  );
};

const AccountNavComponent = ({
  managerPhotoUrl,
  managerInitials,
  logout,
  navigate,
}: {
  managerPhotoUrl?: string;
  managerInitials: string;
  logout: () => void;
  navigate: (to: string) => void;
}) => {
  return (
    <Stack direction={"row"} alignItems={"center"} gap={2} sx={{ flexGrow: 0 }}>
      <Link
        href={config.supportPageUrl}
        target={"_blank"}
        rel={"noopener noreferrer"}
        underline={"none"}
      >
        <Box
          sx={({ palette }) => ({
            color: palette.grey.A700,
            alignItems: "center",
            display: "flex",
            gap: 1,
          })}
        >
          <Typography variant={"body2"}>Support</Typography>
          <OpenInNewOutlined fontSize={"small"} />
        </Box>
      </Link>

      <Tooltip title={"View my profile"}>
        <IconButton onClick={() => navigate("/account")} sx={{ p: 0 }}>
          <ManagerAvatar
            managerPhotoUrl={managerPhotoUrl}
            managerInitials={managerInitials}
          />
        </IconButton>
      </Tooltip>
      <StyledButton
        label={"Log out"}
        size={"small"}
        isGreyStyle
        variant={"text"}
        onClick={logout}
      />
    </Stack>
  );
};

const MobileAccountNavComponent = ({
  navigate,
  logout,
  managerPhotoUrl,
  managerInitials,
}: {
  logout: () => void;
  navigate: (to: string) => void;
  managerPhotoUrl?: string;
  managerInitials: string;
}) => {
  const { isOpen: isAccountMenuOpen, toggle: toggleAccountMenu } =
    useDisclosure();

  const anchorRef = useRef<HTMLButtonElement>(null);

  return (
    <Box sx={{ flexGrow: 0 }}>
      <IconButton onClick={toggleAccountMenu} sx={{ p: 0 }} ref={anchorRef}>
        <ManagerAvatar
          managerPhotoUrl={managerPhotoUrl}
          managerInitials={managerInitials}
        />
      </IconButton>
      <Menu
        sx={({ spacing }) => ({ mt: spacing(4) })}
        id="menu-appbar"
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={isAccountMenuOpen}
        onClose={toggleAccountMenu}
      >
        <MenuItem
          onClick={() => {
            toggleAccountMenu();
            navigate("/account");
          }}
        >
          <SmallTypography>Account</SmallTypography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            toggleAccountMenu();
            window.open(config.supportPageUrl, "_self");
          }}
        >
          <SmallTypography>Support</SmallTypography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            toggleAccountMenu();
            logout();
          }}
        >
          <SmallTypography>Log out</SmallTypography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

const SmallTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  fontFamily: theme.typography.caption.fontFamily,
  textAlign: "center",
}));
